
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import dynamic from 'next/dynamic';
import {useIntersectionObserver} from '@uidotdev/usehooks';
import Link from 'next/link';
import {BlogPost} from '../../../../../../../components/BlogPost';
import BlogImageWithCaption from '../../../../../../../components/BlogImageWithCaption';
import GradientContainer from '../../../../../../../components/GradientContainer';
export const LottiePlayer = dynamic(() => import('../../../../../../../components/LottiePlayer'), {});
export const RiveAnimation = dynamic(() => import('../../../../../../../components/HeroSection/RiveAnimation'), {});
export const meta = {
  title: 'Polar Signals Cloud is Generally Available',
  description: "We're ready to share our product with the world!",
  date: 'October 10, 2023',
  tags: ['GA', 'Generally Available', 'Observability', 'Continuous Profiling', 'eBPF', 'Performance'],
  authors: ['Frederic Branczyk'],
  authorLinks: ['https://twitter.com/fredbrancz'],
  ogImage: '/blog/posts/2023/10/10/ga-announcement.png'
};

const layoutProps = {
  LottiePlayer,
RiveAnimation,
meta
};
const MDXLayout = ({children}) => (
  <BlogPost meta={meta} isMarkdown={true}>
    {children}
  </BlogPost>
)
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <p>{`After a lot of hard work, we're ready to share our product with the world! Our customers are already successfully writing faster software, understanding incidents better and faster, and reducing cloud costs, and now you can too!`}</p>
    <p>{`Whether you are aiming for:`}</p>
    <p>{`🚀 `}<strong parentName="p">{`Fast Software`}</strong>{`: Need your AI workloads, APIs, websites, or any code to run faster?`}</p>
    <p>{`🔎 `}<strong parentName="p">{`Incident Clarity`}</strong>{`: Struggling with hard-to-crack incidents?`}</p>
    <p>{`💰 `}<strong parentName="p">{`Cost Efficiency`}</strong>{`: Want to shave off some zeros from that cloud bill?`}</p>
    <p><strong parentName="p">{`Polar Signals Cloud is for you!`}</strong></p>
    <p><strong parentName="p">{`tl;dr`}</strong>{` Polar Signals Cloud is an always-on, zero-instrumentation continuous profiling product that points you to where in your code CPU time is spent, and helps you maintain an efficient system in the long run. The product is based on the open-source `}<a parentName="p" {...{
        "href": "https://parca.dev/"
      }}>{`Parca`}</a>{` project and is now generally available!`}</p>
    <p>{`If you want to give it a spin, `}<a parentName="p" {...{
        "href": "https://cloud.polarsignals.com/"
      }}>{`sign up for a free 14-day trial`}</a>{`. Continue reading to learn how Polar Signals Cloud is helping our customers today, how it works, and how it is priced.`}</p>
    <GradientContainer className="flex flex-col items-center justify-center rounded-xl py-6" mdxType="GradientContainer">
  <div className="relative mt-8 flex h-[250px] w-full items-center justify-center md:h-[400px] lg:m-0 lg:h-[600px] lg:w-[900px]">
    <RiveAnimation mdxType="RiveAnimation" />
  </div>
    </GradientContainer>
    <h2>{`Why Polar Signals Cloud?`}</h2>
    <h3>{`Writing faster software`}</h3>
    <p>{`If performance is part of the value of your product this is already second nature, but everyone needs to prepare to write more efficient software in a world where we're facing the end of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Moore%27s_law"
      }}>{`Moore's Law`}</a>{` (`}<a parentName="p" {...{
        "href": "http://databasearchitects.blogspot.com/2023/04/the-great-cpu-stagnation.html?m=1"
      }}>{`cost per performance has already stagnated`}</a>{`).`}</p>
    <blockquote>
      <p parentName="blockquote">{`"Polar Signals Cloud has become a critical product in our software lifecycle, from informing how and where to write high-performance code in development, to understanding how it behaves in production and using it to troubleshoot performance-related incidents. Nothing else gives us detail down to the process, thread, and line number and as actionable as Polar Signals Cloud can." - Nikhil Benesch, CTO at Materialize.`}</p>
    </blockquote>
    <BlogImageWithCaption src="/blog/posts/2023/10/10/codeviewer.webp" caption="View profiling data side-by-side with code to instantly understand where the problem lies." mdxType="BlogImageWithCaption" />
    <h3>{`Resolving incidents faster.`}</h3>
    <p>{`Deliver consistently reliable products. With Polar Signals Cloud you always have all performance data available across your entire infrastructure and throughout time. This removes the guesswork and instead allows you to focus on the fix. Understand the difference between version A and B, or two points in time, or two Kubernetes clusters!`}</p>
    <blockquote>
      <p parentName="blockquote">{`"By making the root cause for most performance issues painfully obvious Polar Signals Cloud lets our engineering teams skip the debate about why so they can focus on the fix." - Dan Gillespie, CTO at Indent.`}</p>
    </blockquote>
    <BlogImageWithCaption src="/blog/posts/2023/10/10/diff.webp" caption="Use differential flamegraphs to understand immediately where more or less CPU time is spent." mdxType="BlogImageWithCaption" />
    <h3>{`Reducing the cloud bill.`}</h3>
    <p>{`We can all use a smaller cloud bill. By visualizing all CPU time across the entire infrastructure in one report, Polar Signals Cloud allows you to focus improving only on those parts of the code that will actually have an impact on your cloud bill.`}</p>
    <blockquote>
      <p parentName="blockquote">{`"Using Polar Signals Cloud allowed us to find a fleet-wide 35% CPU improvement only hours after deploying it to production, and has since informed several significant further improvements." - Nikhil Benesch, CTO at Materialize.`}</p>
    </blockquote>
    <BlogImageWithCaption src="/blog/posts/2023/10/10/infra-wide-merge.webp" caption="Viewing fleet-wide CPU usage in a single flamegraph." mdxType="BlogImageWithCaption" />
    <h2>{`How does it work?`}</h2>
    <p>{`Polar Signals Cloud automatically profiles your production infrastructure with no code changes needed, at low overhead (<1%), and supports all major languages (C, C++, Rust, Go, Python, Ruby, Java, Node.js, C#, Erlang, Julia, and `}<a parentName="p" {...{
        "href": "https://www.polarsignals.com/docs/overview#i-already-use-polar-signals-cloud"
      }}>{`more`}</a>{`).`}</p>
    <blockquote>
      <p parentName="blockquote">{`"There's really nothing quite like the magic of Polar Signals Cloud's simplicity. A single agent to deploy, no instrumentation of application code needed. We have a single, consistent solution for everything throughout our stack of languages. With Polar Signals Cloud we can just focus on getting value out of continuous profiling." - Jeroen Schepens, Head of Cloud Infrastructure at Oqton.`}</p>
    </blockquote>
    <div className="flex w-full items-center justify-center rounded-xl bg-gradient-to-br from-[#4C68F6] via-[#609FF4] to-[#74D7F2]">
  <LottiePlayer src="/features/always-on-profiler.json" height="400px" play={true} loop={true} mdxType="LottiePlayer" />
    </div>
    <p>{`The profiler is written using `}<a parentName="p" {...{
        "href": "https://ebpf.io/"
      }}>{`eBPF`}</a>{` and is `}<a parentName="p" {...{
        "href": "https://github.com/parca-dev/parca-agent"
      }}>{`open-source`}</a>{` (with open governance, Apache2 license). We believe the agent being open-source is vital for a component that has to run at high privileges (which eBPF requires).`}</p>
    <p>{`Only an agent needs to be deployed and you can instantly start exploring your data on Polar Signals Cloud.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ kubectl apply -f https://api.polarsignals.com/api/manifests.yaml?token=<your-token>
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`If you want to understand what you're applying here and what each individual Kubernetes object is for, `}<a parentName="p" {...{
          "href": "https://www.polarsignals.com/docs/setup-collection-kubernetes"
        }}>{`check out the docs`}</a>{`.`}</p>
    </blockquote>
    <h2>{`Predictable Pricing`}</h2>
    <p>{`Together with our early customers, we developed our pricing model to be very predictable and easy to understand. It is priced on the amount of profiling data sent to Polar Signals Cloud per month.`}</p>
    <p style={{
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: 32
    }}>$50 / 10GB / month</p>
    <p>{`A fully utilized vCPU produces on average 0.2GB per month or $1 per vCPU per month. Check out our `}<a parentName="p" {...{
        "href": "/pricing/"
      }}>{`pricing calculator`}</a>{`, to see how much it would cost you!`}</p>
    <h2>{`Try Polar Signals Cloud Today!`}</h2>
    <p>{`Polar Signals Cloud offers a 14-day free trial, so what are you waiting for? Build faster, more reliable, and cost-efficient software starting today!`}</p>
    <p className="py-5 text-center">
  <a href="https://cloud.polarsignals.com/" className="hover:bg-brand-dark-blue bg-brand-dark-blue w-full rounded px-8 py-5 text-center text-base font-medium text-white no-underline opacity-95 opacity-100 hover:text-white hover:opacity-100">
    Start Your Free 14-Day Trial
  </a>
    </p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;